/**
 * OUTGOING MESSAGE DIALOG — Dettaglio di una mail della coda in uscita, e la sua modifica.
 *
 * In lettura: destinatari, corpo, motivo dell'errore e, se la mail è partita ed è stata
 * reimportata, il link alla mail vera nella Smart Inbox. Su una mail in attesa o fallita le tre
 * azioni: Invia, Modifica, Annulla.
 *
 * Se la mail risponde a una mail ricevuta nel CRM (POLL-6), il dialogo si allarga e la mostra
 * accanto, in sola lettura, anche durante la modifica: chi controlla la risposta di Filippo ha
 * sott'occhio la domanda. Senza mail di partenza il dialogo resta quello di sempre.
 *
 * La modifica rispetta il formato della mail: testo semplice in un'area di testo, HTML con lo
 * stesso editor del corpo di «Invia email» (il backend lo ripulisce di nuovo). I destinatari si
 * scrivono separati da virgola; ne serve almeno uno, e gli errori del server (400) restano nel
 * dialog.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';
import TextField from '@mui/material/TextField';
import { formatDateTime } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { extractError } from '../../store/thunkHelpers';
import { useIsCoreItemEnabled } from '../../plugin/useCoreItems';
import { outgoingMessageApi } from './outgoingMessageApi';
import { OutgoingMessageSource } from './OutgoingMessageSource';
import { OUTGOING_MESSAGE_STATUSES, isActionable, type OutgoingMessage } from './outgoingMessageTypes';

interface Props {
  outgoingMessage: OutgoingMessage;
  onClose: () => void;
  /** La riga dopo un'azione riuscita: la lista la sostituisce e resta sulla stessa pagina. */
  onChanged: (outgoingMessage: OutgoingMessage) => void;
}

/** «a@b.it, c@d.it» -> ["a@b.it", "c@d.it"], voci vuote tolte. */
function splitAddresses(value: string): string[] {
  return value.split(',').map((a) => a.trim()).filter((a) => a.length > 0);
}

export function OutgoingMessageDialog({ outgoingMessage, onClose, onChanged }: Props) {
  const { t } = useTranslation();
  // Inbox spenta dalla pagina «Voci del CRM» (FILO-53): niente link alla mail nell'inbox.
  const inboxEnabled = useIsCoreItemEnabled('inbox');
  const [editing, setEditing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [subject, setSubject] = useState(outgoingMessage.subject);
  const [body, setBody] = useState(outgoingMessage.body);
  const [to, setTo] = useState(outgoingMessage.to.join(', '));
  const [cc, setCc] = useState(outgoingMessage.cc.join(', '));
  const [bcc, setBcc] = useState(outgoingMessage.bcc.join(', '));

  const status = OUTGOING_MESSAGE_STATUSES.find((s) => s.value === outgoingMessage.status);
  const actionable = isActionable(outgoingMessage.status);
  const hasRecipient = [to, cc, bcc].some((v) => splitAddresses(v).length > 0);
  const sourceId = outgoingMessage.replyToMessageId;
  const title = t(editing ? 'outgoingMessage.editTitle' : 'outgoingMessage.detailTitle');

  async function run(action: () => Promise<OutgoingMessage>) {
    setBusy(true);
    setError(null);
    try {
      const updated = await action();
      setEditing(false);
      setConfirmingCancel(false);
      onChanged(updated);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  }

  function save() {
    void run(() => outgoingMessageApi.update(outgoingMessage.outgoingMessageId, {
      subject, body, to: splitAddresses(to), cc: splitAddresses(cc), bcc: splitAddresses(bcc),
    }));
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth={sourceId !== null ? 'xl' : 'md'} fullWidth
      className="app-dialog">
      <DialogTitle className="tc-head">
        {/* Con la mail di partenza i titoli delle due mail stanno in cima alle colonne, alla stessa
            altezza: in testata resta quello della pagina. */}
        <span className="tc-kicker">{sourceId !== null ? t('nav.surveillance') : title}</span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x" disabled={busy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="app-dialog__body">
        <div className={sourceId !== null ? 'outgoing-split' : undefined}>
          <div className="outgoing-split__main">
            {sourceId !== null && <span className="tc-kicker outgoing-split__title">{title}</span>}
            {error && <p className="text-error">{error}</p>}

            {editing ? (
              <>
                <TextField label={t('outgoingMessage.fields.subject')} value={subject}
                  onChange={(e) => setSubject(e.target.value)} size="small" fullWidth className="form-field" />
                <TextField label={t('outgoingMessage.fields.to')} value={to} onChange={(e) => setTo(e.target.value)}
                  helperText={t('outgoingMessage.addressesHelp')} size="small" fullWidth className="form-field" />
                <TextField label={t('outgoingMessage.fields.cc')} value={cc} onChange={(e) => setCc(e.target.value)}
                  size="small" fullWidth className="form-field" />
                <TextField label={t('outgoingMessage.fields.bcc')} value={bcc} onChange={(e) => setBcc(e.target.value)}
                  size="small" fullWidth className="form-field" />
                {outgoingMessage.htmlFormat ? (
                  <RichTextEditor initialHtml={outgoingMessage.body} onChange={setBody} />
                ) : (
                  <TextField label={t('outgoingMessage.fields.body')} value={body} onChange={(e) => setBody(e.target.value)}
                    multiline minRows={8} fullWidth className="form-field" />
                )}
              </>
            ) : (
              <>
                <p>
                  {status && <Chip size="small" color={status.color} label={t(status.label)} />}{' '}
                  {outgoingMessage.statusDate && formatDateTime(outgoingMessage.statusDate)}
                  {/* Accanto allo stato e non fra le azioni: in fondo, sotto un corpo lungo, non si vedeva. */}
                  {inboxEnabled && outgoingMessage.messageId !== null && (
                    <>
                      {' · '}
                      <Link to={`/inbox?messageId=${outgoingMessage.messageId}`} className="message-activity-body__inbox">
                        <LaunchIcon fontSize="small" />
                        {t('outgoingMessage.actions.openMail')}
                      </Link>
                    </>
                  )}
                </p>
                {outgoingMessage.errorMessage && outgoingMessage.status === 'failed' && (
                  <p className="text-error">{outgoingMessage.errorMessage}</p>
                )}
                <dl>
                  <dt>{t('outgoingMessage.fields.from')}</dt>
                  <dd>
                    {outgoingMessage.senderName ? `${outgoingMessage.senderName} · ` : ''}
                    {outgoingMessage.mailboxEmailAddress ?? t('outgoingMessage.portal')}
                  </dd>
                  <dt>{t('outgoingMessage.fields.to')}</dt>
                  <dd>{outgoingMessage.to.join(', ') || '—'}</dd>
                  {outgoingMessage.cc.length > 0 && (<><dt>{t('outgoingMessage.fields.cc')}</dt><dd>{outgoingMessage.cc.join(', ')}</dd></>)}
                  {outgoingMessage.bcc.length > 0 && (<><dt>{t('outgoingMessage.fields.bcc')}</dt><dd>{outgoingMessage.bcc.join(', ')}</dd></>)}
                  <dt>{t('outgoingMessage.fields.subject')}</dt>
                  <dd>{outgoingMessage.subject}</dd>
                </dl>
                {outgoingMessage.htmlFormat ? (
                  <div className="message-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(outgoingMessage.body) }} />
                ) : (
                  <div className="message-body" style={{ whiteSpace: 'pre-wrap' }}>{outgoingMessage.body}</div>
                )}
                {confirmingCancel && <p className="text-error">{t('outgoingMessage.cancelConfirm')}</p>}
              </>
            )}
          </div>
          {sourceId !== null && <OutgoingMessageSource messageId={sourceId} />}
        </div>
      </DialogContent>

      <DialogActions className="app-dialog__footer">
        {editing ? (
          <>
            <Button variant="outlined" onClick={() => setEditing(false)} disabled={busy}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={save} disabled={busy || !hasRecipient}>{t('common.save')}</Button>
          </>
        ) : confirmingCancel ? (
          <>
            <Button variant="outlined" onClick={() => setConfirmingCancel(false)} disabled={busy}>{t('outgoingMessage.actions.back')}</Button>
            <Button variant="contained" color="error" disabled={busy}
              onClick={() => run(() => outgoingMessageApi.cancel(outgoingMessage.outgoingMessageId))}>
              {t('outgoingMessage.actions.cancel')}
            </Button>
          </>
        ) : actionable ? (
          <>
            <Button variant="outlined" color="error" onClick={() => setConfirmingCancel(true)} disabled={busy}>
              {t('outgoingMessage.actions.cancel')}
            </Button>
            <Button variant="outlined" onClick={() => setEditing(true)} disabled={busy}>
              {t('outgoingMessage.actions.edit')}
            </Button>
            <Button variant="contained" disabled={busy}
              onClick={() => run(() => outgoingMessageApi.send(outgoingMessage.outgoingMessageId))}>
              {t('outgoingMessage.actions.send')}
            </Button>
          </>
        ) : (
          <Button variant="outlined" onClick={onClose}>{t('common.close')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
